import {
  ENTITY_KIND_LABELS,
  ENTITY_KINDS,
  type EntityKind,
  type EntityTypeDef,
  effectiveColorSlot,
  FIELD_TYPE_INFO,
  type FieldDef,
  groupsForType,
  keyFromLabel,
  type SchemaSnapshot,
  typeChain,
} from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Archive, Pencil, Plus, Shapes } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { ColorSlotPicker, Swatch, slotName } from '../components/schema/color-slot-picker';
import { CheckRow, EditorError, Panel, SelectRow, TextRow } from '../components/schema/editor-kit';
import { emptyField, FieldDefEditor, type FieldDraft } from '../components/schema/field-def-editor';
import { Button } from '../components/ui/button';
import { useSchema, useSchemaEdit } from '../lib/schema';

export const Route = createFileRoute('/admin/schema')({
  validateSearch: (s: Record<string, unknown>): { type?: string } => (typeof s.type === 'string' ? { type: s.type } : {}),
  component: SchemaPage,
});

const TOUR = {
  id: 'admin-schema',
  steps: [
    {
      target: '[data-tour="types"]',
      title: 'Types',
      body: 'Every entity has a type. Sub-types inherit their parent’s fields, template and color.',
      learnMore: 'schema-and-templates',
    },
    {
      target: '[data-tour="type-settings"]',
      title: 'Type settings',
      body: 'Label, color (from the palette), and the ID prefix the importer uses to recognise sheets (MED_, ORG_, POL_…).',
    },
    {
      target: '[data-tour="fields"]',
      title: 'Fields',
      body: 'Fields are grouped like the blocks of the profile template. Each field has help, a placeholder and a live preview.',
    },
  ],
};

/** Types of a kind as an indented tree (parents first). */
function tree(types: EntityTypeDef[], kind: EntityKind) {
  const out: { t: EntityTypeDef; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const t of types.filter(x => x.kind === kind && x.parentTypeId === parent).sort((a, b) => a.order - b.order)) {
      out.push({ t, depth });
      walk(t.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

function SchemaPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const { type: selectedKey } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/schema' });
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  if (!snap) return <PageHeader title="Schema" description="Loading…" />;
  const selected = snap.types.find(t => t.key === (selectedKey ?? 'actor')) ?? snap.types[0];
  return (
    <>
      <PageHeader
        title="Schema"
        description="Entity types, their colors and ID prefixes, and the fields every profile shows."
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to="/admin/templates">Profile templates</Link>
          </Button>
        }
      />
      <Tip id="admin-schema" learnMore="schema-and-templates">
        Changes apply everywhere at once. Nothing is deleted: archive types and fields you no longer use, so existing data keeps its
        meaning.
      </Tip>
      <div className="mt-4 grid gap-6 lg:grid-cols-[17rem_1fr]">
        <nav aria-label="Entity types" data-tour="types" className="space-y-4">
          {ENTITY_KINDS.map(kind => (
            <div key={kind}>
              <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{ENTITY_KIND_LABELS[kind]}</h2>
              <ul className="space-y-0.5">
                {tree(snap.types, kind)
                  .filter(({ t }) => showArchived || !t.archived)
                  .map(({ t, depth }) => (
                    <li key={t.id}>
                      <Link
                        to="/admin/schema"
                        search={{ type: t.key }}
                        aria-current={t.id === selected?.id ? 'page' : undefined}
                        className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted aria-[current=page]:bg-muted aria-[current=page]:font-medium"
                        style={{ paddingInlineStart: `${0.5 + depth}rem` }}
                      >
                        <Swatch slot={effectiveColorSlot(snap.types, t.id)} />
                        <span className={t.archived ? 'text-muted-foreground line-through' : ''}>{t.label}</span>
                        {t.codePrefix && <code className="ms-auto text-xs text-muted-foreground">{t.codePrefix}</code>}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
          <CheckRow label="Show archived" checked={showArchived} onChange={setShowArchived} />
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" /> Add type
          </Button>
        </nav>
        <div className="min-w-0 space-y-6">
          {adding && (
            <NewType
              snap={snap}
              parent={selected}
              onDone={key => {
                setAdding(false);
                if (key) navigate({ search: { type: key } });
              }}
            />
          )}
          {selected ? (
            <>
              <TypeSettings key={selected.id} snap={snap} type={selected} />
              <TypeFields snap={snap} type={selected} />
            </>
          ) : (
            <EmptyState icon={Shapes} title="No types yet" learnMore="schema-and-templates" />
          )}
        </div>
      </div>
    </>
  );
}

function NewType({ snap, parent, onDone }: { snap: SchemaSnapshot; parent?: EntityTypeDef; onDone: (key?: string) => void }) {
  const edit = useSchemaEdit();
  const [label, setLabel] = useState('');
  const [parentId, setParentId] = useState(parent?.id ?? '');
  const [prefix, setPrefix] = useState('');
  const [slot, setSlot] = useState<number | null>(null);
  return (
    <Panel title="New type">
      <form
        className="space-y-3"
        onSubmit={e => {
          e.preventDefault();
          edit.mutate(
            { path: '/admin/schema/types', body: { label, parentTypeId: parentId, codePrefix: prefix || null, colorSlot: slot } },
            { onSuccess: () => onDone(keyFromLabel(label)) },
          );
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <TextRow label="Label" required value={label} onChange={setLabel} placeholder="e.g. Lobby group" />
          <SelectRow
            label="Sub-type of"
            value={parentId}
            onChange={setParentId}
            options={snap.types
              .filter(t => !t.archived)
              .map(t => ({
                value: t.id,
                label: `${ENTITY_KIND_LABELS[t.kind]} › ${typeChain(snap.types, t.id)
                  .reverse()
                  .map(x => x.label)
                  .join(' › ')}`,
              }))}
            help="The new type inherits this type's fields, profile template and color."
          />
          <TextRow
            label="ID prefix"
            value={prefix}
            onChange={v => setPrefix(v.toUpperCase())}
            placeholder="e.g. LOB_"
            help="Capital letters ending with _. Optional."
          />
        </div>
        <ColorSlotPicker label="Color" value={slot} onChange={setSlot} noneLabel="Same as parent" />
        <EditorError error={edit.error} />
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={!label.trim() || edit.isPending}>
            Add type
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => onDone()}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function TypeSettings({ snap, type }: { snap: SchemaSnapshot; type: EntityTypeDef }) {
  const edit = useSchemaEdit();
  const [label, setLabel] = useState(type.label);
  const [description, setDescription] = useState(type.description ?? '');
  const [prefix, setPrefix] = useState(type.codePrefix ?? '');
  const [slot, setSlot] = useState<number | null>(type.colorSlot);
  const [parentId, setParentId] = useState(type.parentTypeId ?? '');
  const inherited = effectiveColorSlot(snap.types, type.parentTypeId ?? '');
  const save = () =>
    edit.mutate({
      path: `/admin/schema/types/${type.id}`,
      method: 'PATCH',
      body: {
        label,
        description,
        codePrefix: prefix || null,
        colorSlot: slot,
        ...(type.parentTypeId ? { parentTypeId: parentId } : {}),
      },
    });
  return (
    <div data-tour="type-settings">
      <Panel
        title={
          <span className="flex items-center gap-2">
            <Swatch slot={effectiveColorSlot(snap.types, type.id)} /> {type.label}
            <code className="text-xs font-normal text-muted-foreground">{type.key}</code>
            {type.archived && <span className="rounded bg-muted px-1.5 text-xs font-normal">Archived</span>}
          </span>
        }
        actions={
          type.parentTypeId ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => edit.mutate({ path: `/admin/schema/types/${type.id}`, method: 'PATCH', body: { archived: !type.archived } })}
            >
              <Archive aria-hidden="true" /> {type.archived ? 'Restore' : 'Archive'}
            </Button>
          ) : null
        }
      >
        <form
          className="space-y-3"
          onSubmit={e => {
            e.preventDefault();
            save();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <TextRow label="Label" required value={label} onChange={setLabel} />
            <TextRow
              label="ID prefix"
              value={prefix}
              onChange={v => setPrefix(v.toUpperCase())}
              placeholder="e.g. MED_"
              help="Codes like MED_FRANCTIREUR start with it; the importer pre-selects this type from it."
            />
            {type.parentTypeId && (
              <SelectRow
                label="Sub-type of"
                value={parentId}
                onChange={setParentId}
                options={snap.types
                  .filter(t => t.kind === type.kind && t.id !== type.id && !typeChain(snap.types, t.id).some(a => a.id === type.id))
                  .map(t => ({ value: t.id, label: t.label }))}
              />
            )}
            <TextRow label="Description" value={description} onChange={setDescription} />
          </div>
          <ColorSlotPicker
            label="Color"
            value={slot}
            onChange={setSlot}
            allowNone={!!type.parentTypeId}
            noneLabel={`Same as parent (${slotName(inherited)})`}
          />
          <EditorError error={edit.error} />
          <Button type="submit" size="sm" disabled={edit.isPending}>
            Save type
          </Button>
        </form>
      </Panel>
    </div>
  );
}

function TypeFields({ snap, type }: { snap: SchemaSnapshot; type: EntityTypeDef }) {
  const edit = useSchemaEdit();
  const [editing, setEditing] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState('');
  const chain = typeChain(snap.types, type.id);
  const own = snap.groups.filter(g => g.entityTypeId === type.id).sort((a, b) => a.order - b.order);
  const inherited = groupsForType(snap, chain[1]?.id ?? '', { includeArchived: true });
  const fieldsOf = (groupId: string) => snap.fields.filter(f => f.groupId === groupId).sort((a, b) => a.order - b.order);
  const saveField = (f: FieldDraft, target: { groupId: string } | { fieldId: string }) =>
    edit.mutate(
      'groupId' in target
        ? { path: `/admin/schema/groups/${target.groupId}/fields`, body: f }
        : {
            path: `/admin/schema/fields/${target.fieldId}`,
            method: 'PATCH',
            body: (({ key: _k, dataType: _d, ...rest }) => rest)(f),
          },
      { onSuccess: () => setEditing(null) },
    );
  const reorder = (groupId: string, ids: string[]) => edit.mutate({ path: `/admin/schema/groups/${groupId}/order`, body: { ids } });
  return (
    <div data-tour="fields" className="space-y-4">
      <h2 className="text-lg font-semibold">Fields</h2>
      {inherited.length > 0 && (
        <details className="rounded-lg border bg-surface p-3 text-sm">
          <summary className="cursor-pointer font-medium">
            Inherited from{' '}
            {chain
              .slice(1)
              .map(t => t.label)
              .join(' › ')}{' '}
            ({inherited.reduce((n, g) => n + g.fields.length, 0)} fields)
          </summary>
          <ul className="mt-2 space-y-2">
            {inherited.map(g => (
              <li key={g.key}>
                <span className="font-medium">{g.label}</span>:{' '}
                <span className="text-muted-foreground">{g.fields.map(f => f.label).join(', ')}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">Edit these on the type they come from.</p>
        </details>
      )}
      {own.map(g => {
        const fields = fieldsOf(g.id);
        return (
          <Panel key={g.id} title={g.label}>
            <ul className="divide-y rounded-md border" aria-label={`Fields in ${g.label}`}>
              {fields.map((f, i) => (
                <li key={f.id} className="p-2" aria-label={f.label}>
                  {editing === f.id ? (
                    <FieldDefEditor
                      initial={f}
                      isNew={false}
                      lists={snap.lists}
                      busy={edit.isPending}
                      error={edit.error}
                      onSave={d => saveField(d, { fieldId: f.id })}
                      onCancel={() => setEditing(null)}
                      onArchive={archived =>
                        edit.mutate(
                          { path: `/admin/schema/fields/${f.id}`, method: 'PATCH', body: { archived } },
                          { onSuccess: () => setEditing(null) },
                        )
                      }
                    />
                  ) : (
                    <FieldRow
                      f={f}
                      onEdit={() => {
                        edit.clearError();
                        setEditing(f.id);
                      }}
                      onMove={d => {
                        const ids = fields.map(x => x.id);
                        ids.splice(i, 1);
                        ids.splice(i + d, 0, f.id);
                        reorder(g.id, ids);
                      }}
                      first={i === 0}
                      last={i === fields.length - 1}
                    />
                  )}
                </li>
              ))}
              {fields.length === 0 && <li className="p-3 text-sm text-muted-foreground">No fields in this group yet.</li>}
            </ul>
            {editing === `new:${g.id}` ? (
              <FieldDefEditor
                initial={emptyField()}
                isNew
                lists={snap.lists}
                busy={edit.isPending}
                error={edit.error}
                onSave={d => saveField(d, { groupId: g.id })}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  edit.clearError();
                  setEditing(`new:${g.id}`);
                }}
              >
                <Plus aria-hidden="true" /> Add field to {g.label}
              </Button>
            )}
          </Panel>
        );
      })}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={e => {
          e.preventDefault();
          edit.mutate({ path: `/admin/schema/types/${type.id}/groups`, body: { label: newGroup } }, { onSuccess: () => setNewGroup('') });
        }}
      >
        <div className="w-64">
          <TextRow
            label="New field group"
            value={newGroup}
            onChange={setNewGroup}
            placeholder="e.g. Online presence"
            help="Groups become blocks of the profile template (add the block on the Templates tab)."
          />
        </div>
        <Button type="submit" size="sm" variant="outline" disabled={!newGroup.trim() || edit.isPending}>
          Add group
        </Button>
      </form>
      {!editing && <EditorError error={edit.error} />}
    </div>
  );
}

function FieldRow({
  f,
  onEdit,
  onMove,
  first,
  last,
}: {
  f: FieldDef;
  onEdit: () => void;
  onMove: (d: -1 | 1) => void;
  first: boolean;
  last: boolean;
}) {
  const flags = [
    f.required && 'required',
    f.multiValued && 'several values',
    f.multilingual && 'multilingual',
    f.justificationRequired && 'justification required',
    f.claimKind === 'assessment' && 'assessment',
    f.enumList && `list: ${f.enumList}`,
    f.dataType === 'table' && `${f.columns?.length ?? 0} columns`,
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className={`font-medium ${f.archived ? 'text-muted-foreground line-through' : ''}`}>{f.label}</span>
      <span className="rounded bg-muted px-1.5 text-xs">{FIELD_TYPE_INFO[f.dataType].label}</span>
      {f.archived && <span className="text-xs text-muted-foreground">archived</span>}
      <span className="text-xs text-muted-foreground">{flags.join(' · ')}</span>
      <span className="ms-auto flex gap-1">
        <Button size="sm" variant="ghost" aria-label={`Move ${f.label} up`} disabled={first} onClick={() => onMove(-1)}>
          ↑
        </Button>
        <Button size="sm" variant="ghost" aria-label={`Move ${f.label} down`} disabled={last} onClick={() => onMove(1)}>
          ↓
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Pencil aria-hidden="true" /> Edit<span className="sr-only"> {f.label}</span>
        </Button>
      </span>
    </div>
  );
}
